<template>
  <div :id="computedId">
    <GenericFormModal
      class="mx-5"
      :title="this.title"
      :canSubmit="canSubmit"
      :action="action"
      :isShown="isShown"
      :id="computedId + '-modal'"
      :actionId="clientId"
      :showButtons="!loading"
      @canceled="cancelOperation"
      @submited="onSubmitedData"
    >
      <Loading v-if="loading"></Loading>
      <div v-else>
        <InputTextName
          :label="'sections.formClientSection.label.name' | i18n"
          :identifier="'sections.formClientSection.identifier.name' | i18n"
          :icon="'user circle'"
          @onChange="
            data => {
              nameIsValid = data.validation.isValid;
            }
          "
          :initialValue="dataClient == undefined ? '' : dataClient.nom"
          :forcedValidations="nameForcedValidations"
        />
        <InputSelect
          :isSubmitted="isSubmitted"
          :selectMessage="'sections.formClientSection.label.country' | i18n"
          :options="optionSelectCountry"
          :identifier="'sections.formClientSection.identifier.country' | i18n"
          @onChange="
            data => {
              countryIsValid = data.isValid;
            }
          "
          :selectedOption="dataClient == undefined ? '' : dataClient.pays"
        />
        <InputSelect
          :isSubmitted="isSubmitted"
          :selectMessage="'sections.formClientSection.label.province' | i18n"
          :options="optionSelectProvince"
          :identifier="'sections.formClientSection.identifier.province' | i18n"
          @onChange="
            data => {
              provinceIsValid = data.isValid;
            }
          "
          class="mb-2"
          :selectedOption="dataClient == undefined ? '' : dataClient.province"
        />
        <InputTextName
          :label="'sections.formClientSection.label.city' | i18n"
          :identifier="'sections.formClientSection.identifier.city' | i18n"
          :icon="'building outline'"
          :class="'mb-3'"
          @onChange="
            data => {
              cityIsValid = data.validation.isValid;
            }
          "
          :initialValue="dataClient == undefined ? '' : dataClient.ville"
          :forcedValidations="cityForcedValidations"
        />
        <InputText
          :label="'sections.formClientSection.label.logoUrl' | i18n"
          :identifier="'sections.formClientSection.identifier.logoUrl' | i18n"
          :validations="computedUrlValidations"
          :icon="'server'"
          @onChange="
            data => {
              urlIsValid = data.validation.isValid;
            }
          "
          allowedChars="a-zA-Z0-9/\.:?"
          :initialValue="dataClient == undefined ? '' : dataClient.url_logo"
          :forcedValidations="urlForcedValidations"
        />
        <InputCheckbox
          class="mt-4"
          :name="'muet'"
          :message="'sections.formClientSection.label.mute' | i18n"
          @onChange="
          data => {
            muteIsValid = data.isValid;
          },
        "
          :isChecked="
            dataClient == undefined ? false : Boolean(parseInt(dataClient.muet))
          "
        />
      </div>
    </GenericFormModal>
    <b-toast
      :id="computedToastId"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "sections.formClientSection.error" | i18n }}</span>
      </template>
    </b-toast>
  </div>
</template>

<script>
import GenericFormModal from "@/components/generics/GenericFormModal.vue";
import InputTextName from "@/components/inputs/InputTextName.vue";
import InputCheckbox from "@/components/inputs/InputCheckbox.vue";
import InputSelect from "@/components/inputs/InputSelect.vue";
import InputText from "@/components/inputs/InputText.vue";
import Loading from "@/components/others/Loading.vue";
/**
 * @description Affiche le formulaire pour l'ajout d'un Client.
 * @author Philippe Ménard
 * @author Benjamin Caron
 * @author Alexandre Gallant
 * @vue-prop {String} title - Le titre du formulaire
 * @vue-prop {Function} action - La fonction à faire lors du submit du formulaire
 * @vue-prop {Number} clientId - L'id du client à modifier
 * @vue-prop {Boolean} isShown - Si le formulaire doit être affiché
 * @vue-data {Boolean} isSubmitted - Si le formulaire a déjà été envoyé
 * @vue-data {Boolean} nameIsValid - Si le champ nom est valide
 * @vue-data {Boolean} countryIsValid - Si le champ pays est valide
 * @vue-data {Boolean} provinceIsValid - Si le champ province est valide
 * @vue-data {Boolean} cityIsValid - Si le champ nom ville valide
 * @vue-data {Boolean} urlIsValid - Si le champ url est valide
 * @vue-data {Boolean} muteIsValid - Si le checkbox du muet est valide
 * @vue-data {Number} nameForcedValidations - Nombre de fois que les validations ont été forcées pour «name»
 * @vue-data {Number} cityForcedValidations - Nombre de fois que les validations ont été forcées pour «city»
 * @vue-data {Number} urlForcedValidations - Nombre de fois que les validations ont été forcées pour «url»
 * @vue-data {Array} optionSelectCountry - L'option possible pour le champ pays
 * @vue-data {Array} optionSelectProvince - Les options pour le champ province
 * @vue-data {Object} dataClient - Les données du client à modifier en JSON
 * @vue-data {Boolean} loading - Vrai si la page est en train de charger
 * @vue-computed {String} computedId - L'id à donner au modal
 * @vue-computed {String} computedToastId - L'id du toast
 * @vue-computed {Array} computedUrlValidations - L'objet de validation à utiliser pour valider l'url
 * @vue-event {void} canceled - Est émis lorsque le formulaire est annulé
 * @vue-event {void} successed-request - Est émis lorsque le résultat de la requête est positif (200)
 */
export default {
  name: "FormClientSection",
  components: {
    GenericFormModal,
    InputTextName,
    InputCheckbox,
    InputSelect,
    InputText,
    Loading
  },
  /**
   * @description Obtient les informations nécéssaire pour pouvoir afficher le formulaire
   */
  beforeMount() {
    this.getClient();
  },
  props: {
    title: {
      type: String,
      required: true
    },
    action: {
      type: Function,
      required: true
    },
    clientId: {
      type: Number,
      required: false
    },
    isShown: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isSubmitted: false,
      nameIsValid: false,
      countryIsValid: false,
      provinceIsValid: false,
      cityIsValid: false,
      urlIsValid: false,
      muteIsValid: false,

      nameForcedValidations: 0,
      cityForcedValidations: 0,
      urlForcedValidations: 0,

      optionSelectCountry: ["Canada"],
      optionSelectProvince: [
        "Alberta",
        "Colombie-Britannique",
        "Île-du-Prince-Édouard",
        "Manitoba",
        "Nouveau-Brunswick",
        "Nouvelle-Écosse",
        "Ontario",
        "Québec",
        "Saskatchewan",
        "Terre-Neuve et Labrador"
      ],
      dataClient: {},
      loading: false
    };
  },
  computed: {
    computedId() {
      return "client-" + (this.clientId ? this.clientId : "new");
    },
    computedToastId() {
      return "error-toast-client-" + (this.clientId ? this.clientId : "new");
    },
    computedUrlValidations() {
      return [
        {
          error: this.$i18n.t("errors.empty", {
            name: this.$i18n.t("sections.formClientSection.label.logoUrl")
          }),
          exec: v => v.trim() !== ""
        }
      ];
    }
  },
  methods: {
    /**
     * @description Vérifie si le formulaire peut être envoyé
     * @returns {Boolean} Si le formulaire est valide
     */
    canSubmit() {
      this.isSubmitted = true;
      let canSubmit;
      if (this.clientId != undefined) {
        canSubmit =
          this.nameIsValid ||
          this.countryIsValid ||
          this.provinceIsValid ||
          this.cityIsValid ||
          this.urlIsValid ||
          this.muteIsValid;
      } else {
        canSubmit =
          this.nameIsValid &&
          this.countryIsValid &&
          this.provinceIsValid &&
          this.cityIsValid &&
          this.urlIsValid;
      }
      if (!canSubmit) {
        ++this.nameForcedValidations;
        ++this.cityForcedValidations;
        ++this.urlForcedValidations;
      }
      return canSubmit;
    },
    /**
     * @description Envoie un message d'annulation
     */
    cancelOperation() {
      this.isSubmitted = false;
      this.$emit("canceled");
    },
    /**
     * @description Réaction à la réponse http reçue
     * @param {Object} res La réponse http
     */
    onSubmitedData(res) {
      res.status == 200
        ? this.$emit("successed-request")
        : this.$bvToast.show(this.computedToastId);
    },
    /**
     * @description Fonction qui va chercher les données du client à modifier
     */
    async getClient() {
      if (this.clientId != undefined) {
        this.loading = true;
        await this.$api
          .getClient(this.clientId)
          .then(res => {
            this.dataClient = res.data.data;
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          })
          .finally(() => {
            this.loading = false;
          });
        this.nameIsValid = true;
        this.countryIsValid = true;
        this.provinceIsValid = true;
        this.cityIsValid = true;
        this.urlIsValid = true;
        this.muteIsValid = true;
      }
    }
  }
};
</script>
